import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ChordChart } from "../components/ChordChart";
import { type ChartTimings, rowsFromChart } from "../lib/chartrows";
import { type Analysis, loadAnalysis, type SongRow, titleOf, artistOf } from "../lib/analysis";
import { display, isNoChord } from "../lib/chords";
import { supabase } from "../lib/supabase";
import { AddToSetlist } from "../components/AddToSetlist";
import { ChordDiagram } from "../components/ChordDiagram";
import { Metronome } from "../components/Metronome";
import { Link } from "react-router-dom";

const STAGES: Record<string, string> = {
  queued: "Queued",
  finding: "Finding the recording",
  fetching: "Getting the audio",
  fingerprinting: "Identifying the recording",
  decode: "Decoding",
  beats: "Finding the beat",
  chords: "Working out the chords",
  lyrics: "Looking for the lyrics",
  aligning: "Lining the words up with the music",
};


/** A labelled −/+ pair. Used for transpose and capo, which are the two things
 *  a player reaches for mid-song, so the targets stay finger-sized. */
function Stepper({ label, value, min, max, onChange, format }: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  format: (value: number) => string;
}) {
  return (
    <span className="stepper">
      <span className="stepper-label">{label}</span>
      <button
        className="btn stepper-btn"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label={`${label} down`}
      >−</button>
      <span className="mono stepper-value">{format(value)}</span>
      <button
        className="btn stepper-btn"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label={`${label} up`}
      >+</button>
    </span>
  );
}

export function Song() {
  const { slug = "" } = useParams();
  const [row, setRow] = useState<SongRow | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [transpose, setTranspose] = useState(0);
  const [capo, setCapo] = useState(0);
  const [simple, setSimple] = useState(true);
  const [corrected, setCorrected] = useState(false);
  const [chartText, setChartText] = useState<string | null>(null);
  const [chartTimings, setChartTimings] = useState<ChartTimings | null>(null);
  const [time, setTime] = useState(0);
  const [playAlong, setPlayAlong] = useState(false);
  const player = useRef<any>(null);

  const view = useMemo(() => ({ transpose, capo, simple }), [transpose, capo, simple]);
  const playingRef = useRef(false);
  useEffect(() => { playingRef.current = playAlong; }, [playAlong]);

  // Play along without the video: the chart runs on its own clock, at the pace
  // the recording actually goes, so the words and chords arrive when they do.
  useEffect(() => {
    if (!playAlong || !analysis) return;
    try { player.current?.pauseVideo?.(); } catch { /* no player yet */ }

    let frame = 0;
    let last = performance.now();
    const step = (now: number) => {
      const advanced = (now - last) / 1000;
      last = now;
      setTime((at) => {
        const next = at + advanced;
        if (next >= analysis.duration) { setPlayAlong(false); return analysis.duration; }
        return next;
      });
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [playAlong, analysis?.duration]);

  useEffect(() => {
    let cancelled = false;
    supabase.from("songs").select("*").eq("slug", slug).single()
      .then(({ data }) => { if (!cancelled) setRow(data as SongRow | null); });
    return () => { cancelled = true; };
  }, [slug]);

  // While a song is being worked on, the row itself is the progress bar.
  // Realtime pushes each change, so the page never polls.
  useEffect(() => {
    if (!row || row.status === "done") return;
    const channel = supabase
      .channel(`song:${row.id}`)
      .on("postgres_changes",
          { event: "UPDATE", schema: "public", table: "songs", filter: `id=eq.${row.id}` },
          ({ new: updated }) => setRow(updated as SongRow))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [row?.id, row?.status]);

  useEffect(() => {
    if (row?.analysis_key) loadAnalysis(row).then(setAnalysis);
  }, [row?.analysis_key, row?.lyrics_mode]);

  // Someone chose these chords by hand, extensions and all. Collapsing a
  // corrected Cmaj7 to C hides the very distinction they went in to make, so a
  // corrected song opens showing what it was corrected to.
  // An authored chart replaces the derived one. Without this, editing the
  // words changed nothing anyone could see: the page kept re-deriving its
  // lines from the machine's own transcription.
  useEffect(() => {
    if (!row?.id) return;
    supabase.from("song_chart_current").select("text,timings")
      .eq("song_id", row.duplicate_of ?? row.id).maybeSingle()
      .then(({ data }) => {
        setChartText((data?.text as string) ?? null);
        setChartTimings((data?.timings as ChartTimings) ?? null);
      });
  }, [row?.id]);

  useEffect(() => {
    if (!row?.id) return;
    supabase.from("corrections_current")
      .select("beat", { count: "exact", head: true })
      .eq("song_id", row.duplicate_of ?? row.id)
      .then(({ count }) => {
        if (count && count > 0) { setCorrected(true); setSimple(false); }
      });
  }, [row?.id]);




  // The YouTube player is the clock; the chart follows it.
  useEffect(() => {
    if (!analysis?.source.videoId) return;
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    (window as any).onYouTubeIframeAPIReady = () => {
      player.current = new (window as any).YT.Player("player", {
        videoId: analysis.source.videoId,
        playerVars: { playsinline: 1, modestbranding: 1 },
      });
    };
    const tick = setInterval(() => {
      // While playing along on your own instrument the internal clock owns the
      // time; the paused player would otherwise keep snapping it back.
      if (!playingRef.current && player.current?.getCurrentTime) {
        setTime(player.current.getCurrentTime());
      }
    }, 200);
    return () => clearInterval(tick);
  }, [analysis?.source.videoId]);

  const seek = useCallback((seconds: number) => {
    player.current?.seekTo?.(seconds, true);
    player.current?.playVideo?.();
  }, []);

  if (!row) return <div className="page"><p className="muted">Loading…</p></div>;

  // Chords are published a minute before the words are. Until the lyric clock
  // is final the chart has nothing to hang chords on and renders as one long
  // run of chord names, so the page waits rather than showing that.
  const wordsPending = !row.lyrics_mode || row.lyrics_mode === "pending";
  if (row.status !== "done" || !analysis || wordsPending) {
    return (
      <div className="page">
        <h1 className="serif song-title">{titleOf(row)}</h1>
        <p className="muted">{artistOf(row)}</p>
        <div className="working">
          <span className="spinner" aria-hidden="true"></span>
          <span>
            {row.status === "done" && wordsPending
              ? "Lining the words up with the music"
              : STAGES[row.stage ?? "queued"] ?? "Working"}…
          </span>
        </div>
      </div>
    );
  }

  // Deduplicate on what is SHOWN, not what was detected: simplification and
  // transposition both collapse different labels onto the same grip, so
  // Gm7 and Gm would otherwise draw the same box twice.
  const used = [...new Set(
    analysis.beats.filter((b) => !isNoChord(b.chord))
      .map((b) => display(b.chord, view)),
  )];
  const suggested = analysis.analysis.capo;

  return (
    <div className="page">
      <header className="song-head">
        <div>
          <h1 className="serif song-title">{analysis.title}</h1>
          <p className="serif song-artist">
            <Link to={`/artist/${encodeURIComponent(analysis.artist)}`}>{analysis.artist}</Link>
            {row.credits?.length ? ` with ${row.credits.join(", ")}` : ""}
          </p>
        </div>
        <div className="song-meta mono">
          <div>{analysis.analysis.key} · {Math.round(analysis.analysis.bpm)} bpm · {analysis.analysis.beatsPerBar}/4</div>
          {capo > 0 && <div style={{ color: "var(--accent)" }}>capo {capo}</div>}
          {corrected && <div className="checked" title="Someone has corrected these chords by hand">checked by hand</div>}
        </div>
      </header>

      <div className="controls no-print">
        <Stepper
          label="Transpose" value={transpose} min={-11} max={11}
          onChange={setTranspose}
          format={(value) => (value > 0 ? `+${value}` : String(value))}
        />
        <Stepper
          label="Capo" value={capo} min={0} max={11}
          onChange={setCapo}
          format={(value) => String(value)}
        />
        {suggested > 0 && capo !== suggested && (
          <button className="btn" onClick={() => setCapo(suggested)}>
            Capo {suggested}
          </button>
        )}
        {(transpose !== 0 || capo !== 0) && (
          <button className="btn" onClick={() => { setTranspose(0); setCapo(0); }}>
            Reset
          </button>
        )}
        <span className="divider" />
        <button className="btn" aria-pressed={simple} onClick={() => setSimple((s) => !s)}>
          {simple ? "Simple chords" : "Full chords"}
        </button>
        <button
          className="btn"
          aria-pressed={playAlong}
          onClick={() => setPlayAlong((on) => !on)}
          title={playAlong ? "Stop" : "Scroll the chart in time, without the video"}
        >{playAlong ? "Stop" : "Play along"}</button>
        {playAlong && (
          <button className="btn" onClick={() => setTime(0)} title="Back to the start">
            ⟲
          </button>
        )}
        <Metronome
          bpm={analysis.analysis.bpm}
          beatsPerBar={analysis.analysis.beatsPerBar}
        />
        <button className="btn" onClick={() => window.print()}>Print</button>
        <AddToSetlist songId={row.id} />
      </div>

      {(analysis.lyrics.meta as Record<string, unknown> | null)?.wordTimings === "unverified" && (
        <p className="muted small" style={{ borderLeft: "2px solid var(--accent)", paddingLeft: 10 }}>
          The word timings for this song came from the lyrics provider, not from
          this recording — neither the aligner nor transcription could read the
          audio. Chords may sit a beat or two out. The chords themselves are
          measured from the recording and are unaffected.
        </p>
      )}

      <div className="shapes">
        {used.map((shown) => {
          return (
            <figure key={shown} className="shape">
              <ChordDiagram label={shown} />
              <figcaption className="mono">{shown}</figcaption>
            </figure>
          );
        })}
      </div>

      {suggested > 0 && (
        <p className="muted small">
          Capo {suggested} suggested — play {(analysis.analysis.capoShapes ?? []).slice(0, 5).join(" ")}.
          Worked out from the chord shapes and the ringing open strings, not measured.
        </p>
      )}

      <div id="player" className="player no-print" />

      <ChordChart
        analysis={analysis}
        rows={chartText ? rowsFromChart(chartText, analysis, view, chartTimings) : undefined}
        view={view}
        currentTime={time}
        onSeek={seek}
        follow={playAlong}
      />

      <p className="muted small no-print">
        A chord in the wrong place?{" "}
        <Link to={`/song/${slug}/edit`}>Open the editor</Link> — every chord in
        the song, with the history of who changed what.
      </p>

    </div>
  );
}
